import React, { useRef, useEffect } from "react";
import { Form, Input, InputNumber, Select, Checkbox, Typography } from "antd";
import { useIntl } from "react-intl";
import { getFormItemVaidation, getLocalPhoneNumber } from "../util/helpers";

const { Text } = Typography;
const { Option } = Select;

const EditableCell = ({
  title,
  type,
  inputType,
  formItem,
  listData,
  listDataId,
  listDataName,
  selectMode,
  selectData,
  editable,
  children,
  dataIndex,
  record,
  required,
  handleSave,
  editing,
  focused,
  built_in_non_editable,
  min,
  precision,
  formItemRules,
  disabledFields,
  requiredFields,
  ...restProps
}) => {
  const inputRef = useRef();
  const intl = useIntl();

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  const restricted =
    record &&
    record.built_in &&
    built_in_non_editable &&
    built_in_non_editable.includes(dataIndex);
  const requiredMessage = `${title} ${intl.formatMessage({
    id: "app.forms.requiredMessage",
  })}`;
  const selected =
    selectData && record[dataIndex] !== null
      ? selectData.find((i) => i.value === record[dataIndex])
      : null;

  const isDisabled = disabledFields && disabledFields.includes(dataIndex);
  const rules = getRules(
    intl,
    dataIndex,
    formItemRules,
    requiredFields,
    required,
    requiredMessage
  );

  const selectedFromList = getSelectedFromList(
    listData,
    listDataId,
    listDataName,
    selectMode,
    record,
    dataIndex
  );

  let childNode = children;

  if (editable) {
    childNode =
      editing && !restricted
        ? renderEditableContent(
            type,
            formItem,
            dataIndex,
            rules,
            focused,
            inputRef,
            isDisabled,
            min,
            precision,
            selectData
          )
        : renderReadOnlyContent(
            selectedFromList,
            selected,
            type,
            inputType,
            record,
            dataIndex,
            intl
          );
  }

  return <td {...restProps}>{childNode}</td>;
};

const getRules = (
  intl,
  dataIndex,
  formItemRules,
  requiredFields,
  required,
  requiredMessage
) => {
  let rules = formItemRules
    ? getFormItemVaidation(intl, dataIndex, formItemRules)
    : [];
  if ((requiredFields && requiredFields.includes(dataIndex)) || required) {
    rules.push({
      required: true,
      pattern: /^\s*\S.*$/,
      message: requiredMessage,
    });
  }
  return rules;
};

const getSelectedFromList = (
  listData,
  listDataId,
  listDataName,
  selectMode,
  record,
  dataIndex
) => {
  if (!listData || !listDataId || !listDataName) return null;

  if (selectMode !== "multiple") {
    const filtered = listData.find((i) => i[listDataId] === record[dataIndex]);
    return filtered && filtered[listDataName];
  }

  if (record[dataIndex] && record[dataIndex].length > 0) {
    const filtered = listData.filter((i) =>
      record[dataIndex].includes(i[listDataId])
    );
    return filtered.map((elem) => elem.name).join(", ");
  }

  return " ";
};

const renderEditableContent = (
  type,
  formItem,
  dataIndex,
  rules,
  focused,
  inputRef,
  isDisabled,
  min,
  precision,
  selectData
) => {
  if (type === "formItem") return formItem;

  return (
    <Form.Item
      style={{ margin: 0 }}
      name={dataIndex}
      rules={rules}
      valuePropName={type === "checkbox" ? "checked" : "value"}
    >
      {renderInputByType(
        type,
        focused,
        inputRef,
        isDisabled,
        min,
        precision,
        selectData
      )}
    </Form.Item>
  );
};

const renderInputByType = (
  type,
  focused,
  inputRef,
  isDisabled,
  min,
  precision,
  selectData
) => {
  switch (type) {
    case "number":
      return (
        <InputNumber
          ref={focused ? inputRef : null}
          min={min}
          precision={precision}
          disabled={isDisabled}
        />
      );
    case "select":
      return (
        <Select disabled={isDisabled}>
          {selectData.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.name}
            </Option>
          ))}
        </Select>
      );
    case "checkbox":
      return <Checkbox />;
    default:
      return <Input ref={focused ? inputRef : null} disabled={isDisabled} />;
  }
};

const renderReadOnlyContent = (
  selectedFromList,
  selected,
  type,
  inputType,
  record,
  dataIndex,
  intl
) => {
  if (selectedFromList) {
    return (
      <Text ellipsis={{ tooltip: selectedFromList }}>{selectedFromList}</Text>
    );
  }
  if (selected) {
    return <Text ellipsis={{ tooltip: selected.name }}>{selected.name}</Text>;
  }
  if (type === "checkbox") {
    return <Checkbox checked={record[dataIndex]} disabled />;
  }
  if (inputType === "phone") {
    const phoneNumber =
      record[dataIndex] && record[dataIndex] !== ""
        ? getLocalPhoneNumber(record[dataIndex].toString())
        : "";
    return <Text ellipsis={{ tooltip: phoneNumber }}>{phoneNumber}</Text>;
  }
  if (type === "number") {
    return record[dataIndex];
  }
  if (record[dataIndex]) {
    return intl.formatMessage({
      id: `app.settings.system_tables.${record[dataIndex]}`,
      defaultMessage: record[dataIndex].toString(),
    });
  }
  return "";
};

export default EditableCell;
